import InfoIcon from '@mui/icons-material/Info';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';

interface PublicTendersFiltersProps {
  searchTerm: string;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  sortConfig: { key: string; direction: 'asc' | 'desc' };
  onSortChange: (event: SelectChangeEvent<string>) => void;
  sourceFilter: string;
  onSourceFilterChange: (value: string) => void;
  stateFilter: string[];
  onStateFilterChange: (value: string[]) => void;
  hideClosedTenders: boolean;
  onHideClosedTendersChange: (value: boolean) => void;
  onSaveSearch: () => void;
  hasKeywords: boolean;
}

const PublicTendersFilters: React.FC<PublicTendersFiltersProps> = ({
  searchTerm,
  onSearchChange,
  sortConfig,
  onSortChange,
  sourceFilter,
  onSourceFilterChange,
  stateFilter,
  onStateFilterChange,
  hideClosedTenders,
  onHideClosedTendersChange,
  onSaveSearch,
  hasKeywords,
}) => {
  const theme = useTheme();

  return (
    <Grid container spacing={2} sx={{ mt: 2 }} alignItems="center">
      {/* Row 1: Search and Sort */}
      <Grid size={{ xs: 12, md: 9 }}>
        <TextField
          label="Search tenders (title, issuer, keyword, ID)"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={onSearchChange}
          fullWidth
          sx={{
            '& .MuiInputLabel-root': {
              color: theme.palette.text.primary,
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: theme.palette.primary.dark,
            },
            '& .MuiOutlinedInput-root': {
              backgroundColor: theme.palette.greyLight,
              borderRadius: 3,
              '& fieldset': {
                borderColor: theme.palette.text.primary,
                borderRadius: 3,
              },
              '&:hover fieldset': {
                borderColor: theme.palette.common.black,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.bidlogiq?.primaryOrange || theme.palette.primary.main,
              },
              '& .MuiInputBase-input': {
                color: `${theme.palette.text.primary} !important`,
              },
              '&.Mui-focused .MuiInputBase-input': {
                color: `${theme.palette.text.primary} !important`,
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <SearchIcon
                sx={{
                  mr: 1,
                  color: theme.palette.bidlogiq?.primaryOrange || theme.palette.primary.main,
                }}
              />
            ),
            endAdornment: (
              <Tooltip
                title={
                  <Typography sx={{ fontSize: '1rem' }}>
                    <strong>Search Capabilities:</strong>
                    <br />
                    Search across Title, Issuer, Keywords, Tender ID, Scope, Description, and State.
                    <br />
                    <br />
                    <strong>Boolean Search:</strong>
                    <br />
                    Use <strong>AND</strong> / <strong>OR</strong> / <strong>NOT</strong> to refine
                    results.
                    <br />
                    Examples:
                    <br />
                    <em>"Construction AND Road"</em>
                    <br />
                    <em>"Software OR IT"</em>
                    <br />
                    <em>"Cleaning NOT Window"</em>
                  </Typography>
                }
                arrow
                placement="top"
              >
                <InfoIcon
                  sx={{
                    color: theme.palette.text.secondary,
                    cursor: 'help',
                    '&:hover': { color: theme.palette.primary.main },
                  }}
                />
              </Tooltip>
            ),
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 3 }}>
        <FormControl variant="outlined" size="small" fullWidth sx={{ borderRadius: 3 }}>
          <InputLabel id="sort-by-label">Sort By</InputLabel>
          <Select
            labelId="sort-by-label"
            id="sort-by-select"
            value={`${sortConfig.key}-${sortConfig.direction}`}
            onChange={onSortChange}
            label="Sort By"
            sx={{
              backgroundColor: theme.palette.greyLight,
              borderRadius: 3,
              '& .MuiSelect-select': { color: theme.palette.text.primary },
              '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
              fieldset: { borderColor: theme.palette.text.primary, borderRadius: 3 },
            }}
          >
            <MenuItem
              value="relevance-desc"
              disabled={!hasKeywords}
              sx={{
                fontStyle: !hasKeywords ? 'italic' : 'normal',
                color: !hasKeywords ? theme.palette.text.disabled : theme.palette.text.primary,
              }}
            >
              {hasKeywords ? 'Relevance' : 'Relevance (Add tenders to enable)'}
            </MenuItem>
            <MenuItem value="llmProcessingTimestamp-desc">Added (Newest First)</MenuItem>
            <MenuItem value="llmProcessingTimestamp-asc">Added (Oldest First)</MenuItem>
            <MenuItem value="submissionDeadline-asc">Deadline (Closest First)</MenuItem>
            <MenuItem value="submissionDeadline-desc">Deadline (Furthest First)</MenuItem>
            <MenuItem value="title-asc">Title (A-Z)</MenuItem>
            <MenuItem value="title-desc">Title (Z-A)</MenuItem>
            <MenuItem value="client-asc">Issuer (A-Z)</MenuItem>
            <MenuItem value="client-desc">Issuer (Z-A)</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* Row 2: Source, Locale, Save */}
      <Grid size={{ xs: 12, md: 2 }}>
        <FormControl variant="outlined" size="small" fullWidth sx={{ borderRadius: 3 }}>
          <InputLabel id="source-filter-label">Source</InputLabel>
          <Select
            labelId="source-filter-label"
            id="source-filter-select"
            value={sourceFilter}
            onChange={(e) => onSourceFilterChange(e.target.value as string)}
            label="Source"
            sx={{
              backgroundColor: theme.palette.greyLight,
              borderRadius: 3,
              '& .MuiSelect-select': { color: theme.palette.text.primary },
              '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
              fieldset: { borderColor: theme.palette.text.primary, borderRadius: 3 },
            }}
          >
            <MenuItem value="ALL">All Sources</MenuItem>
            <MenuItem value="VP">VendorPanel</MenuItem>
            <MenuItem value="ATM">AusTender</MenuItem>
            <MenuItem value="BNSW">Buy NSW</MenuItem>
            <MenuItem value="TVIC">Tenders Vic</MenuItem>
            <MenuItem value="TWA">Tenders WA</MenuItem>
            <MenuItem value="SAT">SA Tenders</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12, md: 5 }}>
        <FormControl variant="outlined" size="small" fullWidth sx={{ borderRadius: 3 }}>
          <InputLabel id="region-filter-label">Filter by Region</InputLabel>
          <Select
            labelId="region-filter-label"
            id="region-filter-select"
            multiple
            value={stateFilter}
            onChange={(e) =>
              onStateFilterChange(
                typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
              )
            }
            label="Filter by Region"
            renderValue={(selected) =>
              selected.length === 0 ? (
                <Typography variant="body2">All Regions</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )
            }
            sx={{
              backgroundColor: theme.palette.greyLight,
              borderRadius: 3,
              '& .MuiSelect-select': { color: theme.palette.text.primary },
              '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
              fieldset: { borderColor: theme.palette.text.primary, borderRadius: 3 },
            }}
          >
            {['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'].map((name) => (
              <MenuItem key={name} value={name}>
                <Checkbox checked={stateFilter.includes(name)} size="small" />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12, md: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={hideClosedTenders}
              onChange={(e) => onHideClosedTendersChange(e.target.checked)}
              size="small"
              sx={{
                color: theme.palette.text.primary,
                '&.Mui-checked': {
                  color: theme.palette.bidlogiq?.primaryOrange || theme.palette.primary.main,
                },
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
              Hide Closed Tenders
            </Typography>
          }
          sx={{ m: 0, height: '100%', display: 'flex', alignItems: 'center' }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 2 }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={onSaveSearch}
          fullWidth
          sx={{
            height: '35px',
            textTransform: 'none',
            backgroundColor: theme.palette.greyContrast,
            color: theme.palette.primary.contrastText,
            borderRadius: 3,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: theme.palette.text.primary,
              color: theme.palette.primary.contrastText,
            },
          }}
        >
          Save Search
        </Button>
      </Grid>
    </Grid>
  );
};

export default PublicTendersFilters;
